import { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"
import { theme } from "../../../../styles/theme"
import { HistoryPeriodType, HistorySafeScore } from "../../../../types"
import { useGetDriveStaticAvgDate } from "../../../../api/history"
import { Chart, ChartData, ChartOptions } from "chart.js"
import { Line } from "react-chartjs-2"
import { axios, toQueryString } from "../../../../api"

interface Props {
  deviceId: number,
  periodType: HistoryPeriodType,
  date: string
  onEvent?: (e: any) => any,
}

export const Section_History_PeriodAverage = ({deviceId, periodType, date, onEvent}: Props) => {
  const _ref = useRef({
    onEvent
  }).current,
  ref = useRef({
    periodTypes: [
      {label: "일별", value: "D"},
      {label: "주별", value: "W"},
      {label: "월별", value: "M"},
    ],
    scoresApiState: "idle" as "idle" | "loading",
    scoresApiParams: {
      deviceId: 0,
      type: "",
      date: "",
      count: 0,
    },
    scores: [] as HistorySafeScore[],
    createAvgApiParams(deviceId: number, scores: HistorySafeScore[], scoreIndex: number) {
      const avgApiParams = {
        deviceId,
        startDate: "",
        endDate: "",
      }
  
      if (scores.length > 0) {
        const score = scores[scoreIndex]
        avgApiParams.startDate = score.startDate.replace("T", " ")
        avgApiParams.endDate = score.endDate.replace("T", " ")
      }
  
      return {avgApiParams}
    },
    createAvg(avgApiRes: any) {
      const result: {label: string, value: string}[] = []
      result.push({label: "평균운행시간", value: avgApiRes?.avgDrivetime ?? "-"})
      result.push({label: "총운행시간", value: avgApiRes?.sumDrivetime ?? "-"})
      result.push({label: "연속운전2시간초과횟수", value: avgApiRes?.driveTwoHour ?? "-"})
      result.push({label: "평균운행거리", value: avgApiRes?.avgDistance ?? "-"})
      result.push({label: "총운행거리", value: avgApiRes?.totalDistance ?? "-"})
      result.push({label: "평균속도", value: avgApiRes?.avgSpeed ?? "-"})
      result.push({label: "평균이벤트", value: avgApiRes?.avgEvent ?? "-"})
      result.push({label: "평균과속시간", value: avgApiRes?.overSpeedTimeAvg ?? "-"})
      result.push({label: "평균과속거리", value: avgApiRes?.overSpeedDisAvg ?? "-"})
      result.push({label: "급정거", value: avgApiRes?.fastStop ?? "-"})
      return result
    },
  }).current,
  _chartRef = useRef({
    defaultChartData: {
      labels: [],
      datasets: [
        {
          data: [],
          borderColor: theme.colors.yellowgreen,
          borderWidth: 3,
          pointBackgroundColor: theme.colors.black_bg,
          pointBorderColor: theme.colors.yellowgreen,
          pointBorderWidth: 3,
          pointRadius: 6,
          fill: "start",
          clip: 7,
          backgroundColor(context, options) {
            const chart = context.chart,
            {ctx, chartArea} = chart
            if (!chartArea) return
            const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
            gradient.addColorStop(0, "rgba(0, 152, 121, 0.05)")
            gradient.addColorStop(1, "rgba(0, 152, 121, 0.5)")
            return gradient
          },
        }
      ]
    } as ChartData<"line">,
    defaultChartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      clip: false,
      scales: {
        x: {
          reverse: true,
          min: 0,
          max: 9,
          bounds: "ticks",
          ticks: {
            color: theme.colors.white,
            padding: 10,
          },
          grid: {
            color: theme.colors.trans,
          }
        },
        y: {
          min: 0,
          max: 100,
          ticks: {
            color: theme.colors.white,
            maxTicksLimit: 10,
          },
          grid: {
            color: theme.colors.black_bg2,
          },
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
        title: {
          display: false
        },
        legend: {
          display: false
        },
      },
      onHover(e, activeElements) {
        (e?.native?.target as HTMLElement).style.cursor = activeElements?.length > 0 ? 'pointer' : 'auto';
      },
      onClick: (event, elements, chart) => {
        const scoreIndex = elements[0]?.index
        if (Number.isInteger(scoreIndex)) {
          setActiveScore(ref.scores[scoreIndex])
          chart.options.scales!.x!.ticks!.color = ({index, tick}) => {
            return tick.value == scoreIndex ? theme.colors.yellowgreen : theme.colors.white
          }
          chart.update()
        }
      },
    } as ChartOptions<"line">,
  }).current,
  chartRef = useRef<Chart<"line"> | null>(null),
  [activeScore, setActiveScore] = useState<HistorySafeScore | null>(null),
  avgApiParams = useMemo(() => {
    const result = {
      deviceId: ref.scoresApiParams.deviceId,
      startDate: "",
      endDate: "",
    }

    if (activeScore) {
      result.startDate = activeScore.startDate.replace("T", " ")
      result.endDate = activeScore.endDate.replace("T", " ")
    }

    return result
  }, [activeScore]),
  {data: avgApiRes} = useGetDriveStaticAvgDate(avgApiParams),
  avg = useMemo(() => ref.createAvg(avgApiRes), [avgApiRes])

  _ref.onEvent = onEvent

  // 안전점수차트 관련 캐싱 초기화 및 초기 데이터 요청
  useEffect(() => {
    if (deviceId > 0 && !!periodType && !!date) {
      ref.scoresApiState = "loading"
      ref.scoresApiParams = {
        deviceId,
        type: periodType,
        date: date.replace(/-/g, ""),
        count: 0,
      }
      const endpoint = `history/driveSafeScoreListV2?${toQueryString(ref.scoresApiParams)}`
      const chart = chartRef.current
      if (chart) {
        try {
          chart.data.labels = []
          chart.data.datasets[0].data = ref.scores = []
          chart.options.scales!.x!.min = 0
          chart.options.scales!.x!.max = 9
          chart.update()
        } catch (e) {
          console.error(`안전점수차트 초기화 에러`)
        }
      }
      axios.main<HistorySafeScore[]>(endpoint)
        .then(({data}) => {
          const newActiveScoreIndex = 0
          ref.scores = data
          setActiveScore(ref.scores[newActiveScoreIndex])
          if (chart) {
            for (const item of data) {
              chart.data.labels!.push(item.startDate.split("T")[0])
              chart.data.datasets[0].data.push(Number(item.safeScore))
              chart.options.scales!.x!.ticks!.color = ({tick}) => {
                return tick.value == newActiveScoreIndex ? theme.colors.yellowgreen : theme.colors.white
              }
            }
            chart.update()
          }
        })
        .catch(e => console.error(e))
        .finally(() => {ref.scoresApiState = "idle"})
    }
  }, [deviceId, periodType, date])
  // 드래그시, 안전점수 추가 데이터 요청 및 병합
  useEffect(() => {
    const chart = chartRef.current
    const threshold = 50
    let startX: number | null = null
    const onmousedown = (e: MouseEvent) => {
      if (e.target == chart?.canvas) startX = e.clientX
    }
    const onmousemove = (e: MouseEvent) => {
      const
      x = e.clientX,
      xScales = chart?.options?.scales?.x
      if (xScales && startX != null) {
        const
        xDiff = startX - x,
        count = Math.floor(Math.abs(xDiff) / threshold),
        length = chart.data.datasets[0].data.length
        
        if (count > 0 && length > 0) {
          const
          isMovingRight = xDiff < 0,
          dir = isMovingRight ? 1 : -1,
          min = Number.parseInt(String(xScales.min)) + dir,
          displayCount = 10,
          max = min + displayCount - 1
          // 데이터 추가 요청
          if (max > (length - 6) && ref.scoresApiState == "idle") {
            ref.scoresApiState = "loading"
            const params = {...ref.scoresApiParams}
            params.count++
            const endpoint = `history/driveSafeScoreListV2?${toQueryString(params)}`
            axios.main<HistorySafeScore[]>(endpoint)
              .then(({data}) => {
                if (params.deviceId == ref.scoresApiParams.deviceId)
                if (params.type == ref.scoresApiParams.type)
                if (params.date == ref.scoresApiParams.date)
                if ((params.count - 1) == ref.scoresApiParams.count) {
                  ref.scoresApiParams = params
                  for (const item of data) {
                    ref.scores.push(item)
                    chart.data.labels!.push(item.startDate.split("T")[0])
                    chart.data.datasets[0].data.push(Number(item.safeScore))
                  }
                  chart.update()
                }
              })
              .catch(e => console.error(e))
              .finally(() => {ref.scoresApiState = "idle"})
          }
          // 스크롤 효과
          if ((0 <= min) && (min <= (length - displayCount))) {
            xScales.min = min
            xScales.max = max
            chart.update()
          }
          startX = x
        }
      }
    }
    const onmouseup = (e: MouseEvent) => {
      startX = null
    }
    document.addEventListener("mousedown", onmousedown)
    document.addEventListener("mousemove", onmousemove)
    document.addEventListener("mouseup", onmouseup)
    return () => {
      document.removeEventListener("mousedown", onmousedown)
      document.removeEventListener("mousemove", onmousemove)
      document.removeEventListener("mouseup", onmouseup)
    }
  }, [])
  // 현재 선택된 안전점수 전파
  useEffect(() => {
    if (_ref.onEvent) {
      _ref.onEvent({
        type: "safetyScore",
        data: activeScore?.safeScore ?? null
      })
    }
  }, [activeScore])

  const onClick: MouseEventHandler = e => {
    try {
      const {target} = e
      if (target instanceof HTMLElement) {
        const
        {dataset} = target,
        {onclick} = dataset
        if (onclick) switch (onclick) {
          case "period_type": {
            const value = (e.target as HTMLLIElement)?.dataset.value || ""
            if (onEvent && value && (value != periodType)) {
              onEvent({
                section: "PeriodAverage",
                type: "periodType",
                data: {value}
              })
            }
            return
          }
        }
      }

    } catch (e: any) {
      console.error(`onPeriodTypeClick > `, e)
    }
  }

  return (
    <SSection className="PeriodAverage" onClick={onClick}>
      <header>
        <label>주행통계</label>
        <ol>
          {ref.periodTypes.map(({label, value}) => {
            return (
              <li className={periodType == value ? "active" : ""} key={value} data-value={value} data-onclick="period_type">{label}</li>
            )
          })}
        </ol>
      </header>

      <ul>
        {avg.map(({label, value}) => {
          return (
            <li key={label}>
              <label>{label}</label>
              <strong>{value}</strong>
            </li>
          )
        })}
      </ul>

      <div className="chart">
        <Line ref={chartRef} data={_chartRef.defaultChartData} options={_chartRef.defaultChartOptions} />
      </div>
    </SSection>
  )
}

const SSection = styled.section`
  position: relative;
  grid-template-rows: auto auto 1fr;

  & > header {
    & > ol {
      display: flex;
      color: white;

      & > li {
        cursor: pointer;
        padding: 0 5px;
        font-weight: 600;

        &.active {
          color: ${theme.colors.yellowgreen};
        }
      }
    }
  }
  & > ul {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px;

    & > li {
      flex: 1;
      flex-shrink: 0;
      display: grid;
      justify-items: center;
      border: 1px solid ${theme.colors.yellowgreen};
      border-radius: 10px;
      padding: 6px 10px;
      white-space: nowrap;

      & > span {
        color: ${theme.colors.white};
      }
      & > strong {
        color: ${theme.colors.yellowgreen};
      }
    }
  }
  & > .chart {
    padding: 10px;
    display: grid;

    & canvas {
      max-width: 100%;
      height: 180px;
    }
  }
`